import React, { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../api";
import { useAuth } from "../AuthContext";
import { AlertOctagon, CheckCircle2 } from "lucide-react";

const EMPTY = {
  category:"", partner_name:"", subject:"", description:"",
  client_name:"", client_email:"", photos:[], videos:[],
};

export default function ComplaintPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    ...EMPTY,
    client_name: user?.name || "",
    client_email: user?.email || "",
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState("");
  const [video, setVideo] = useState("");

  const addUrl = (field, val, clear) => {
    if (!val || !/^https?:\/\//i.test(val)) return toast.error("Cole uma URL válida");
    setForm({ ...form, [field]: [...form[field], val] }); clear("");
  };
  const removeUrl = (field, i) => setForm({ ...form, [field]: form[field].filter((_, idx)=>idx!==i) });

  const submit = async () => {
    if (!form.category || !form.partner_name || !form.subject || !form.description)
      return toast.error("Preencha os campos obrigatórios");
    setBusy(true);
    try {
      await axios.post(`${API}/complaints`, form, { withCredentials: true });
      setSent(true);
      toast.success("Reclamação registrada!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <div className="card-arya p-10">
          <CheckCircle2 className="mx-auto text-[color:var(--success)]" size={40}/>
          <h1 className="font-serif text-3xl mt-4">Reclamação registrada</h1>
          <p className="text-[color:var(--ink-500)] mt-3">Nossa equipe fará a análise e entrará em contato quando houver atualização.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-14">
      <div className="text-center mb-8">
        <div className="tag-sun mb-3 inline-block">OUVIDORIA</div>
        <h1 className="font-serif text-4xl flex items-center justify-center gap-2"><AlertOctagon size={28} className="text-[color:var(--danger)]"/> Registrar reclamação</h1>
        <p className="text-[color:var(--ink-500)] mt-3">Conte o que aconteceu. Sua manifestação é importante para melhorarmos a qualidade dos nossos parceiros.</p>
      </div>

      <div className="card-arya p-6 space-y-3">
        <input placeholder="Categoria do parceiro *" value={form.category} onChange={e=>setForm({...form, category:e.target.value})}
               className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="cmp-category"/>
        <input placeholder="Nome do parceiro ou empresa *" value={form.partner_name} onChange={e=>setForm({...form, partner_name:e.target.value})}
               className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="cmp-partner"/>
        <input placeholder="Assunto *" value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})}
               className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="cmp-subject"/>
        <textarea rows="4" placeholder="Descrição e comentários detalhados *" value={form.description} onChange={e=>setForm({...form, description:e.target.value})}
                  className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="cmp-desc"/>
        <div className="grid sm:grid-cols-2 gap-2">
          <input placeholder="Seu nome" value={form.client_name} onChange={e=>setForm({...form, client_name:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          <input placeholder="Seu email" value={form.client_email} onChange={e=>setForm({...form, client_email:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
        </div>
        <UrlAdder label="Fotos (URL)" input={photo} setInput={setPhoto} onAdd={()=>addUrl("photos", photo, setPhoto)} items={form.photos} onRemove={(i)=>removeUrl("photos", i)}/>
        <UrlAdder label="Vídeos (URL)" input={video} setInput={setVideo} onAdd={()=>addUrl("videos", video, setVideo)} items={form.videos} onRemove={(i)=>removeUrl("videos", i)}/>
        <button onClick={submit} disabled={busy} className="btn-gold w-full" data-testid="btn-cmp-submit">
          {busy ? "Enviando..." : "Enviar reclamação"}
        </button>
      </div>
    </div>
  );
}

function UrlAdder({ label, input, setInput, onAdd, items, onRemove }) {
  return (
    <div>
      <label className="text-xs text-[color:var(--ink-500)]">{label}</label>
      <div className="flex gap-2">
        <input value={input} onChange={e=>setInput(e.target.value)} placeholder="https://..." className="flex-1 rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
        <button onClick={onAdd} className="btn-ghost !py-2 !px-4 text-sm">Adicionar</button>
      </div>
      {items.length>0 && <ul className="mt-1 text-xs text-[color:var(--ink-500)]">{items.map((u,i)=><li key={i} className="flex gap-2"><span className="truncate flex-1">{u}</span><button onClick={()=>onRemove(i)} className="text-[color:var(--danger)]">×</button></li>)}</ul>}
    </div>
  );
}
